import {
  HP_DISPLAY_MAX,
  UNIT_KINDS,
  forecastCodexMatchup,
  type TileKind,
  type UnitKind,
} from "@dailywars/core/engine";
import { useState } from "react";

import { SectionHeader } from "#/components/SectionHeader";

import type { CodexCalculatorColumnProps } from "./-codex.damageCalculator.columns";
import { CODEX_ATTACKER_UNIT_KINDS, pickTerrainForUnit } from "./-codex.damageCalculator.fields";
import { CodexDamageCalculatorCard } from "./-codex.damageCalculator.layout";

function useCodexDamageCalculatorState() {
  const [attackerKind, setAttackerKind] = useState<UnitKind>("rifleman");
  const [attackerTerrain, setAttackerTerrain] = useState<TileKind>("plain");
  const [attackerDisplayHp, setAttackerDisplayHp] = useState(HP_DISPLAY_MAX);
  const [defenderKind, setDefenderKind] = useState<UnitKind>("rifleman");
  const [defenderTerrain, setDefenderTerrain] = useState<TileKind>("plain");
  const [defenderDisplayHp, setDefenderDisplayHp] = useState(HP_DISPLAY_MAX);

  const forecast = forecastCodexMatchup({
    attackerKind,
    attackerDisplayHp,
    defenderKind,
    defenderDisplayHp,
    defenderTileKind: defenderTerrain,
    attackerTileKind: attackerTerrain,
  });

  const attackerProps = {
    player: "p1",
    unitKind: attackerKind,
    unitKinds: CODEX_ATTACKER_UNIT_KINDS,
    terrain: attackerTerrain,
    displayHp: attackerDisplayHp,
    onUnitKindChange: (kind: UnitKind) => {
      setAttackerKind(kind);
      setAttackerTerrain(pickTerrainForUnit({ kind, current: attackerTerrain }));
    },
    onTerrainChange: setAttackerTerrain,
    onDisplayHpChange: setAttackerDisplayHp,
  } satisfies CodexCalculatorColumnProps;

  const defenderProps = {
    player: "p2",
    unitKind: defenderKind,
    unitKinds: UNIT_KINDS,
    terrain: defenderTerrain,
    displayHp: defenderDisplayHp,
    onUnitKindChange: (kind: UnitKind) => {
      setDefenderKind(kind);
      setDefenderTerrain(pickTerrainForUnit({ kind, current: defenderTerrain }));
    },
    onTerrainChange: setDefenderTerrain,
    onDisplayHpChange: setDefenderDisplayHp,
  } satisfies CodexCalculatorColumnProps;

  return { forecast, attackerProps, defenderProps, attackerDisplayHp };
}

export function CodexDamageCalculatorSection() {
  const { forecast, attackerProps, defenderProps, attackerDisplayHp } =
    useCodexDamageCalculatorState();

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader>Damage Calculator</SectionHeader>
      <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
        Pick a matchup to see expected damage and gold trade.
      </p>
      <CodexDamageCalculatorCard
        forecast={forecast}
        attackerProps={attackerProps}
        defenderProps={defenderProps}
        attackerDisplayHp={attackerDisplayHp}
      />
    </section>
  );
}
